import React, { useEffect }  from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { motion } from "framer-motion";
import fintechHero from "../../../Assets/fintech-hero.png";   // main hero image
import fintech2 from "../../../Assets/fintech2.png";
import fintechFlow from "../../../Assets/fintechflow.png";
import { COLORS } from "../../../Constants/colors";
import KEYWORDS from "../../../Constants/keywords";

// ---------- Animations ----------
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const staggerList = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0, transition: { duration: 0.5 } },
};

// ---------- Styled Components ----------
const PageWrapper = styled.div`
  background: radial-gradient(circle at top, #1a1a1a, #0d0d0d 70%);
  color: ${COLORS.textPrimary};
  padding: 2rem 1rem;
  min-height: 100vh;
`;

const HeroSection = styled(motion.section)`
  max-width: 1200px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  img {
    max-width: 70%;
    border-radius: 20px;
    margin-top: 1.5rem;
    box-shadow: 0 12px 12px rgba(255, 106, 0, 0.1);
    transform: perspective(1000px) rotateX(2deg);
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: ${COLORS.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.8rem;

  @media (max-width: 768px) {
    font-size: 2.3rem;
  }
`;

const Intro = styled.p`
  font-size: 1.3rem;
  line-height: 1.7;
  max-width: 950px;
  margin: 2rem auto;
  color: ${COLORS.textSecondary};
`;

const SectionsWrapper = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr)); // auto-fit two sections in row
  gap: 2rem;
  max-width: 1200px;
  margin: 3rem auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr; // stack on mobile
  }
`;

const Section = styled(motion.section)`
  padding: 2rem;
  border-radius: 18px;
  background: linear-gradient(135deg, #141414, #1c1c1c 70%, rgba(255, 106, 0, 0.3) 120%);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-6px);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
  color: #fff;
  background: ${COLORS.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const List = styled(motion.ul)`
  margin: 0;
  padding-left: 1.2rem;
  line-height: 1.7;
  list-style: none;

  li {
    margin-bottom: 0.6rem;
    color: ${COLORS.textPrimary};
    position: relative;
    padding-left: 1.8rem;
    font-size: 1.1rem;

    &::before {
      content: "⚡";
      position: absolute;
      left: 0;
      color: ${COLORS.primary};
      font-size: 1.2rem;
    }
  }
`;

const TechGrid = styled(motion.div)`

  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 1.5rem;
`;

const TechCard = styled(motion.div)`
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  background: rgba(255, 106, 0, 0.1);
  border: 1px solid rgba(255, 106, 0, 0.4);
  color: ${COLORS.textPrimary};
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.25s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 106, 0, 0.2);
    box-shadow: 0 0 8px rgba(255, 106, 0, 0.4);
    transform: scale(1.05);
  }
`;

const ImageBlock = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin: 3rem 0;

  img {
    max-width: 85%;
    border-radius: 22px;
    box-shadow: 0 12px 35px rgba(255, 106, 0, 0.2);
    transform: perspective(1000px) rotateY(-3deg);
  }
`;

// ---------- Config Data ----------
const fintechDetailConfig = {
  title: "FinTech & NBFC",
  intro:
    "Virtuosoft Technologies specialize in building cutting-edge FinTech and NBFC Solutions. Combining deep FinTech & NBFC domain expertise, Virtuosoft provides end-to-end product development — from secure transaction processing to compliance-ready architectures — enabling financial institutions to scale faster and serve their customers better.",
  images: {
    hero: fintechHero,
    primary: fintech2,
    secondary: fintechFlow,
  },
  sections: [
    {
      title: "Technology Enablement",
      items: [
        "Loan Origination System (LOS)",
        "Loan Management System (LMS)",
        "Disbursal Management",
        "Core Banking Solutions",
        "API Integration",
      ],
    },
    {
      title: "B2B & B2C Gold Purchase",
      items: [
        "Auction / Retail Purchase of Gold and ultimate selling to refiners",
        "User role-specific Reports and Dashboard",
        "Rule and eligibility checking for eligible amount",
        "Rule engine for complex approval matrix",
        "Flow engine implementation for workflows",
      ],
    },
    {
      title: "Customer Acquisition & Onboarding",
      items: [
        "Central Customer Repository",
        "Digital KYC (eKYC / Video KYC)",
        "AI-Based Credit Scoring Models",
        "Lead Management & CRM Tools",
        "Omni-Channel Platforms",
      ],
    },
    {
      title: "Payments & Collections",
      items: ["Payment Gateways", "UPI AutoPay / NACH", "Digital Disbursements", "POS Integration"],
    },
    {
      title: "Analytics & Risk Management",
      items: ["Fraud Detection & Prevention", "Portfolio Analytics", "Regulatory Reporting Automation"],
    },
    {
      title: "Comprehensive Card & Payment Technology Expertise",
      items: [
        "Core Transaction System Development: Vision-PLUS",
        "Card Lifecycle Management",
        "Authorization, Clearing & Settlement workflows",
        "Installment Billing & EMI Support",
        "Loyalty & Rewards Integration",
        "Dispute handling & chargeback resolution",
        "ISO 8583 Protocol Implementation",
        "Smart Card Wallet Systems",
      ],
    },
  ],
  technologies: [
    ".NET Core 8.0 C# / Node.js",
    "Angular / React",
    "AI / ML: Google Gemini / OpenAI GPT",
    "MS SQL Server / Postgres SQL / MongoDB",
    "Microsoft Azure / AWS / GCP",
    "Microservice Architecture",
    "Kubernetes",
    "RabbitMQ / AWS SQS",
    "Redis",
    "Elasticsearch",
  ],
};

// ---------- Main Component ----------
function FinTechDetailPage() {
  useEffect(() => {
      window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <meta name="keywords" content={KEYWORDS.fintech.join(", ")} />
      </Helmet>
      <PageWrapper>
        {/* Hero Section */}
        <HeroSection initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
          <Title>{fintechDetailConfig.title}</Title>
          <motion.img
            src={fintechDetailConfig.images.hero}
            alt="FinTech Hero"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
          <Intro>{fintechDetailConfig.intro}</Intro>
        </HeroSection>

        <ImageBlock {...fadeInUp}>
          <motion.img
            src={fintechDetailConfig.images.primary}
            alt="FinTech Flow"
            whileHover={{ scale: 1.03, rotateY: 2 }}
            transition={{ type: "spring", stiffness: 120 }}
          />
        </ImageBlock>

        {/* Sections */}
        <SectionsWrapper>
          {fintechDetailConfig.sections.map((section, idx) => (
            <Section key={idx} {...fadeInUp}>
              <SectionTitle>{section.title}</SectionTitle>
              <List variants={staggerList} initial="hidden" whileInView="show" viewport={{ once: false, amount: 0.2 }}>
                {section.items.map((item, i) => (
                  <motion.li key={i} variants={staggerItem}>
                    {item}
                  </motion.li>
                ))}
              </List>
            </Section>
          ))}
        </SectionsWrapper>

        {/* Secondary Image */}
        <ImageBlock {...fadeInUp}>
          <motion.img
            src={fintechDetailConfig.images.secondary}
            alt="FinTech Flow"
            whileHover={{ scale: 1.03, rotateY: 2 }}
            transition={{ type: "spring", stiffness: 120 }}
          />
        </ImageBlock>

        {/* Technologies */}
        <SectionsWrapper>
          <Section {...fadeInUp}>
            <SectionTitle>Technology Stack</SectionTitle>
            <TechGrid variants={staggerList} initial="hidden" whileInView="show" viewport={{ once: false, amount: 0.2}}>
              {fintechDetailConfig.technologies.map((tech, index) => (
                <TechCard key={index} variants={staggerItem}>
                  {tech}
                </TechCard>
              ))}
            </TechGrid>
          </Section>
        </SectionsWrapper>
      </PageWrapper>
    </>
  );
}

export default FinTechDetailPage;
